import { borderRadius, color as fplColor } from '@figma/fpl-tokens/tokens.stylex';
import * as stylex from '@stylexjs/stylex';
import { Box } from '@/UI/Box/Box';
import { CardMedia } from '@/UI/Card/Card';
import { color } from '@/colors';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { useIsHovered } from '@/hooks/useIsHovered';
import { defaultWorkflowCover } from '../utils/defaultCover';
import type { ReactNode } from 'react';

interface WorkflowCardDisplayProps {
  poster?: string;
  hoverOverlay?: ReactNode;
  forceShowOverlay?: boolean;
  /**
   * FPL branch only: hover state supplied by the parent. Under `CardPrimitive.Root` the thumbnail is a
   * `pointer-events: none` descendant, so its own `onMouseEnter`/`onMouseLeave` never fire — the parent
   * owns the hover trigger (widened to the whole card) and passes the resolved state down.
   */
  isHovered?: boolean;
}

const styles = stylex.create({
  container: {
    // Real CSS border (not inset box-shadow): the full-bleed thumbnail <img> paints over an inset
    // shadow and occludes it, so the border must be a real border painted around the content box.
    // box-sizing keeps the 1px border from growing the fixed 240px height.
    border: `1px solid ${fplColor.border}`,
    borderRadius: borderRadius.large,
    boxSizing: 'border-box',
    height: 240,
    overflow: 'hidden',
    position: 'relative',
  },
  image: {
    display: 'block',
    height: '100%',
    objectFit: 'cover',
    width: '100%',
  },
  overlay: {
    backgroundColor: fplColor.bgTransparentHover,
    inset: 0,
    pointerEvents: 'none',
    position: 'absolute',
  },
});

export default function WorkflowCardDisplay({
  poster,
  hoverOverlay,
  forceShowOverlay,
  isHovered: isHoveredProp,
}: WorkflowCardDisplayProps) {
  const isFplMigrationEnabled = useFplMigrationFlag();
  const { isHovered, ...hoveredProps } = useIsHovered();

  if (isFplMigrationEnabled === true) {
    // When rendered inside CardPrimitive.Root the thumbnail is a `pointer-events: none` descendant, so
    // its own mouse handlers never fire — the parent owns hover and passes it via `isHovered`. When
    // rendered standalone (e.g. ArchiveWorkflowCard's plain container), no parent controls hover, so we
    // fall back to self-managed hover and attach the handlers ourselves.
    const isControlled = isHoveredProp !== undefined;
    const hovered = isControlled ? isHoveredProp : isHovered;
    return (
      <div {...stylex.props(styles.container)} {...(isControlled ? {} : hoveredProps)}>
        <img {...stylex.props(styles.image)} src={poster || defaultWorkflowCover(true)} alt="workflow poster" />
        {hovered && !hoverOverlay && <div {...stylex.props(styles.overlay)} />}
        {(hovered || forceShowOverlay) && hoverOverlay}
      </div>
    );
  }

  return (
    <>
      <Box
        {...hoveredProps}
        sx={{
          overflow: 'hidden',
          border: '1px solid',
          borderRadius: 2,
          borderColor: isHovered ? color.White08_T : color.Dark_Grey,
          height: 240,
          position: 'relative',
        }}
      >
        <CardMedia component="img" height="100%" image={poster || defaultWorkflowCover(false)} alt="workflow poster" />
        {isHovered && !hoverOverlay && (
          <Box sx={{ position: 'absolute', inset: 0, backgroundColor: color.White16_T, pointerEvents: 'none' }} />
        )}
        {(isHovered || forceShowOverlay) && hoverOverlay}
      </Box>
    </>
  );
}
